import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "./baseQuery";

export interface HelpCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  articleCount: number;
}

export interface Article {
  _id: string;
  title: string;
  content: string;
  moduleCode: string;
  imageUrl?: string;
  isPublished?: boolean;
  views?: number;
  helpful?: number | null;
  hasVoted?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface SupportTicket {
  id: string;
  email: string;
  category: string;
  description: string;
  status: "open" | "in-progress" | "resolved" | "closed";
  assignedTo?: string;
  response?: string;
  createdAt: string;
  updatedAt: string;
}

// Mock Data
const MOCK_CATEGORIES: HelpCategory[] = [
  // ... existing categories
  {
    id: "getting-started",
    name: "Getting Started",
    description: "Learn the basics of using the HRMS platform",
    icon: "Rocket",
    articleCount: 12,
  },
  {
    id: "employee-management",
    name: "Employee Management",
    description: "Manage employee profiles and records",
    icon: "Users",
    articleCount: 8,
  },
  {
    id: "time-attendance",
    name: "Time & Attendance",
    description: "Track work hours, leaves, and time off",
    icon: "Clock",
    articleCount: 15,
  },
];

const MOCK_ARTICLES: Article[] = [
  {
    _id: "article-1",
    moduleCode: "GETTING_STARTED",
    title: "How to set up your profile",
    content: "Setting up your profile is the first step...",
    createdAt: "2026-04-10T10:00:00Z",
    updatedAt: "2026-04-10T10:00:00Z",
  }
]

const MOCK_TICKETS: SupportTicket[] = [
  {
    id: "TKT-001",
    email: "john.doe@example.com",
    category: "time-attendance",
    description: "I'm unable to apply for leave for next week. It says 'Insufficient balance' even though I have 5 days left.",
    status: "open",
    createdAt: "2026-04-15T09:00:00Z",
    updatedAt: "2026-04-15T09:00:00Z",
  },
  {
    id: "TKT-002",
    email: "jane.smith@example.com",
    category: "getting-started",
    description: "How do I change my profile picture?",
    status: "in-progress",
    assignedTo: "Admin Team",
    createdAt: "2026-04-16T10:30:00Z",
    updatedAt: "2026-04-17T11:00:00Z",
  }
];

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

export const helpCenterApi = createApi({
  reducerPath: "helpCenterApi",
  baseQuery,
  tagTypes: ["Categories", "Articles", "Tickets", "Feedback"],
  endpoints: (builder) => ({
    getPublicArticles: builder.query<
      Record<string, Article[]>,
      void
    >({
      query: () => ({
        url: "/v2/help-center/public/articles",
        method: "GET",
      }),
      transformResponse: (response: any) => response.data,
      providesTags: ["Articles"],
    }),
    getPublicArticleById: builder.query<Article, string>({
      query: (id) => ({
        url: `/v2/help-center/articles/${id}`,
        method: "GET",
      }),
      transformResponse: (response: any) => response.data,
      providesTags: (result, error, arg) => [{ type: "Articles", id: arg }],
    }),

    // Content endpoints (Public / User) - DO NOT TOUCH
    getCategories: builder.query<HelpCategory[], void>({
      queryFn: async () => {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return { data: MOCK_CATEGORIES };
      },
      providesTags: ["Categories"],
    }),
    getArticles: builder.query<Article[], string | undefined>({
      queryFn: async (categoryId) => {
        await new Promise((resolve) => setTimeout(resolve, 500));
        const filtered = categoryId
          ? MOCK_ARTICLES.filter((a) => a.moduleCode === categoryId)
          : MOCK_ARTICLES;
        return { data: filtered };
      },
      providesTags: ["Articles"],
    }),
    getArticleById: builder.query<Article, string>({
      query: (id) => ({
        url: `/v2/help-center/articles/${id}`,
        method: "GET",
      }),
      transformResponse: (res: any) => res.data,
      providesTags: (result, error, arg) => [{ type: "Articles", id: arg }],
    }),

    submitFeedback: builder.mutation<
      any,
      { moduleCode: string; description: string }
    >({
      query: (body) => ({
        url: "/v2/help-center/feedback",
        method: "POST",
        body,
      }),
    }),

    // Tickets (Public) - DO NOT TOUCH
    submitSupportTicket: builder.mutation<
      { ticketId: string },
      { email: string; category: string; description: string }
    >({
      queryFn: async (data) => {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        const id = `TKT-${Math.floor(Math.random() * 10000)}`;
        MOCK_TICKETS.push({
          ...data,
          id,
          status: "open",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
        return { data: { ticketId: id } };
      },
      invalidatesTags: ["Tickets"],
    }),

    // Admin CRUD - Articles
    getAdminArticles: builder.query<
      PaginatedResponse<Article>,
      { page?: number; limit?: number; search?: string; moduleCode?: string; isPublished?: boolean }
    >({
      query: (params) => ({
        url: "/v2/help-center/articles",
        method: "GET",
        params,
      }),
      providesTags: ["Articles"],
    }),

    createArticle: builder.mutation<
      Article,
      {
        title: string;
        content: string;
        moduleCode: string;
        imageUrl?: string;
        isPublished?: boolean;
      }
    >({
      query: (body) => ({
        url: "/v2/help-center/articles",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Articles"],
    }),

    updateArticle: builder.mutation<
      Article,
      {
        id: string;
        title?: string;
        content?: string;
        moduleCode?: string;
        imageUrl?: string;
        [key: string]: any;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/v2/help-center/articles/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Articles"],
    }),

    uploadArticleImage: builder.mutation<
      { success: boolean; data: { fileUrl: string; key: string } },
      FormData
    >({
      query: (formData) => ({
        url: "/v2/help-center/articles/upload",
        method: "POST",
        body: formData,
      }),
    }),

    deleteArticle: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/v2/help-center/articles/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Articles"],
    }),

    voteArticle: builder.mutation<
      { success: boolean; message: string },
      { id: string; helpful: boolean }
    >({
      query: ({ id, helpful }) => ({
        url: `/v2/help-center/articles/${id}/vote`,
        method: "POST",
        body: { helpful },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Articles", id },
        "Articles",
      ],
    }),

    // Admin CRUD - Feedback
    getFeedback: builder.query<
      PaginatedResponse<any>,
      {
        page?: number;
        limit?: number;
        search?: string;
        moduleCode?: string;
        status?: string;
      }
    >({
      query: (params) => ({
        url: "/v2/help-center/feedback",
        method: "GET",
        params,
      }),
      transformResponse: (response: any) => response.data,
      providesTags: ["Feedback"],
    }),

    updateFeedbackStatus: builder.mutation<
      any,
      { id: string; status: string }
    >({
      query: ({ id, status }) => ({
        url: `/v2/help-center/feedback/${id}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: ["Feedback"],
    }),

    getModules: builder.query<{ code: string; name: string }[], void>({
      query: () => ({
        url: "/v2/help-center/modules",
        method: "GET",
      }),
      transformResponse: (response: any) => response.data,
    }),

    // Deprecated / Mock Admin Endpoints (Optional to keep, but user said ONLY implement admin-related ones above)
    // I will keep the original code for Tickets/Categories if they were used by Admin as well,
    // but the user's list is the definitive one for new APIs.
    getAllTickets: builder.query<SupportTicket[], void>({
      queryFn: async () => {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return { data: MOCK_TICKETS };
      },
      providesTags: ["Tickets"],
    }),
    getTicketById: builder.query<SupportTicket | undefined, string>({
      queryFn: async (id) => {
        await new Promise((resolve) => setTimeout(resolve, 500));
        const ticket = MOCK_TICKETS.find((t) => t.id === id);
        return { data: ticket };
      },
      providesTags: (result, error, arg) => [{ type: "Tickets", id: arg }],
    }),
    updateTicketStatus: builder.mutation<
      SupportTicket,
      { id: string; updates: Partial<SupportTicket> }
    >({
      queryFn: async ({ id, updates }) => {
        await new Promise((resolve) => setTimeout(resolve, 500));
        const index = MOCK_TICKETS.findIndex((t) => t.id === id);
        if (index !== -1) {
          MOCK_TICKETS[index] = {
            ...MOCK_TICKETS[index],
            ...updates,
            updatedAt: new Date().toISOString(),
          };
          return { data: MOCK_TICKETS[index] };
        }
        return { error: { status: 404, data: "Ticket not found" } as any };
      },
      invalidatesTags: (result, error, arg) => [
        "Tickets",
        { type: "Tickets", id: arg.id },
      ],
    }),
  }),
});

export const {
  useGetCategoriesQuery,
  useGetArticlesQuery,
  useGetArticleByIdQuery,
  useSubmitSupportTicketMutation,
  useGetPublicArticlesQuery,
  useGetPublicArticleByIdQuery,
  useSubmitFeedbackMutation,
  useVoteArticleMutation,
  // Admin exports
  useGetAdminArticlesQuery,
  useCreateArticleMutation,
  useUpdateArticleMutation,
  useDeleteArticleMutation,
  useUploadArticleImageMutation,
  useGetFeedbackQuery,
  useGetModulesQuery,
  useUpdateFeedbackStatusMutation,
  // Existing exports kept to avoid breaking shared usage
  useGetAllTicketsQuery,
  useGetTicketByIdQuery,
  useUpdateTicketStatusMutation,
} = helpCenterApi;
